import { z } from "zod";
import { compileExpr } from "./expr.js";

/**
 * Figure mathématique décrite par l'enseignant : courbes y = f(x), curseurs, point mobile.
 * Stockée dans le bloc « figure » de la leçon (spec) : elle voyage avec le contenu,
 * fonctionne hors ligne et se dessine à l'identique dans l'app et dans le studio.
 */

const ParamName = z.string().regex(/^[a-zA-Z]\w{0,7}$/, "Nom de curseur : une lettre puis lettres ou chiffres (8 au plus)")
  .refine((n) => !["x", "pi", "e"].includes(n), "Ce nom est réservé (x, pi, e)");
const Range = z.tuple([z.number().finite(), z.number().finite()]).refine(([a, b]) => b > a && b - a <= 1000, "Intervalle invalide");

export const FigureSlider = z.object({
  name: ParamName,
  label: z.record(z.string().max(60)).default({}),
  min: z.number().finite(),
  max: z.number().finite(),
  step: z.number().positive().max(100).default(0.1),
  initial: z.number().finite(),
}).refine((s) => s.max > s.min && s.initial >= s.min && s.initial <= s.max, "Curseur : min < max et valeur initiale entre les deux");

export const FIGURE_COLORS = ["teal", "amber", "ink", "muted"] as const;
export const FigureCurve = z.object({
  expr: z.string().min(1).max(200),
  color: z.enum(FIGURE_COLORS).default("teal"),
  style: z.enum(["solid", "dashed"]).default("solid"),
  label: z.string().max(40).optional(),
});

export const FigureSpec = z.object({
  kind: z.literal("function-plot"),
  view: z.object({ x: Range, y: Range }),
  sliders: z.array(FigureSlider).max(4).default([]),
  curves: z.array(FigureCurve).min(1).max(4),
  /** Point que l'élève déplace le long d'une courbe ; ses coordonnées s'affichent. */
  point: z.object({ curve: z.number().int().min(0).max(3), initialX: z.number().finite() }).nullable().default(null),
  /** Points remarquables posés sur une courbe (sommet, racine…) : l'ordonnée est toujours calculée, jamais saisie. */
  marks: z.array(z.object({ curve: z.number().int().min(0).max(3), x: z.number().finite(), label: z.string().max(24).optional() })).max(6).optional(),
}).superRefine((s, ctx) => {
  const names = s.sliders.map((x) => x.name);
  if (new Set(names).size !== names.length) ctx.addIssue({ code: "custom", message: "Deux curseurs portent le même nom" });
  s.curves.forEach((c, i) => {
    const r = compileExpr(c.expr, ["x", ...names]);
    if (!r.ok) ctx.addIssue({ code: "custom", path: ["curves", i, "expr"], message: `Courbe ${i + 1} : ${r.error}` });
  });
  if (s.point && s.point.curve >= s.curves.length) ctx.addIssue({ code: "custom", path: ["point"], message: "Le point mobile suit une courbe qui n'existe pas" });
  if (s.marks?.some((m) => m.curve >= s.curves.length)) ctx.addIssue({ code: "custom", path: ["marks"], message: "Un point remarquable est posé sur une courbe qui n'existe pas" });
});
export type FigureSpec = z.infer<typeof FigureSpec>;

/** Figure par défaut (et lecture des anciennes leçons dont la spec est vide) : la parabole y = a·x². */
export const DEFAULT_FIGURE: FigureSpec = {
  kind: "function-plot",
  view: { x: [-4, 4], y: [-4, 8] },
  sliders: [{ name: "a", label: { fr: "Coefficient a", wo: "Coefficient a" }, min: -3, max: 3, step: 0.1, initial: 1 }],
  curves: [{ expr: "x^2", color: "muted", style: "dashed" }, { expr: "a*x^2", color: "teal", style: "solid" }],
  point: { curve: 1, initialX: 1 },
};

/** Modèles proposés dans le studio pour démarrer vite. */
export const FIGURE_PRESETS: { id: string; label: string; spec: FigureSpec }[] = [
  { id: "parabola", label: "Parabole y = a·x²", spec: DEFAULT_FIGURE },
  {
    id: "affine", label: "Droite y = a·x + b",
    spec: {
      kind: "function-plot", view: { x: [-5, 5], y: [-5, 5] },
      sliders: [
        { name: "a", label: { fr: "Coefficient directeur a" }, min: -3, max: 3, step: 0.1, initial: 1 },
        { name: "b", label: { fr: "Ordonnée à l'origine b" }, min: -4, max: 4, step: 0.5, initial: 0 },
      ],
      curves: [{ expr: "a*x + b", color: "teal", style: "solid" }],
      point: { curve: 0, initialX: 1 },
    },
  },
  {
    id: "sine", label: "Sinusoïde y = A·sin(k·x)",
    spec: {
      kind: "function-plot", view: { x: [-7, 7], y: [-4, 4] },
      sliders: [
        { name: "A", label: { fr: "Amplitude A" }, min: 0, max: 3, step: 0.1, initial: 1 },
        { name: "k", label: { fr: "Pulsation k" }, min: 0.5, max: 3, step: 0.1, initial: 1 },
      ],
      curves: [{ expr: "A*sin(k*x)", color: "teal", style: "solid" }],
      point: null,
    },
  },
];

/** Lit la spec d'un bloc figure ; une spec absente ou invalide retombe sur la parabole (anciennes leçons). */
export function readFigureSpec(raw: unknown): FigureSpec {
  const r = FigureSpec.safeParse(raw);
  return r.success ? r.data : DEFAULT_FIGURE;
}

export const initialParams = (s: FigureSpec) => Object.fromEntries(s.sliders.map((x) => [x.name, x.initial]));
